/**
 * FilmAdminComps Page
 * Management page for film comps with quick access to upcoming/recent films
 * and search for historical films
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getFilmTrackingOverview } from '../../modules/FilmTracking/services/filmTrackingService';
import FilmSearch from '../../components/Assignment/FilmSearch';
import CompAssignmentModal from '../../components/Assignment/CompAssignmentModal';
import apiService from '../../lib/services/apiService';

const FilmAdminComps = () => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Quick List, 1 = Search
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilm, setSelectedFilm] = useState(null);
  const [showCompModal, setShowCompModal] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [compsMap, setCompsMap] = useState({}); // filmId -> comps array

  // Calculate date range: 2 weeks ago to 6 weeks from now
  const getDateRange = () => {
    const today = new Date();
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(today.getDate() - 14);
    const sixWeeksFromNow = new Date(today);
    sixWeeksFromNow.setDate(today.getDate() + 42);
    
    return {
      startDate: twoWeeksAgo.toISOString().split('T')[0],
      endDate: sixWeeksFromNow.toISOString().split('T')[0]
    };
  };

  // Fetch films for quick list
  const fetchFilms = async () => {
    setLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = getDateRange();
      const result = await getFilmTrackingOverview({
        startDate,
        endDate,
        sortBy: 'release_date',
        sortOrder: 'asc',
        limit: 500
      });
      setFilms(result.films || []);
      
      // Fetch comps for all films
      await fetchCompsForFilms(result.films || []);
    } catch (err) {
      console.error('Error fetching films:', err);
      setError('Failed to load films. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch comps for multiple films
  const fetchCompsForFilms = async (filmList) => {
    const compsPromises = filmList.map(async (film) => {
      try {
        const filmId = film.imdb_id || film.imdbId || film.tt_id;
        if (!filmId) return null;
        
        const response = await apiService.get(`/comps/films/${filmId}/comps?type=greenlight`);
        
        if (response.success && response.data) {
          return { filmId, comps: response.data.greenlightComps || [] };
        }
        return { filmId, comps: [] };
      } catch (err) {
        console.error(`Error fetching comps for ${film.imdb_id}:`, err);
        return { filmId: film.imdb_id || film.imdbId || film.tt_id, comps: [] };
      }
    });

    const results = await Promise.all(compsPromises);
    const map = {};
    results.forEach(({ filmId, comps }) => {
      if (filmId) {
        map[filmId] = comps;
      }
    });
    setCompsMap(map);
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchFilms();
    }
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddComp = (film) => {
    setSelectedFilm(film);
    setEditingComp(null);
    setShowCompModal(true);
  };

  const handleEditComp = (film, comp) => {
    setSelectedFilm(film);
    setEditingComp(comp);
    setShowCompModal(true);
  };

  const handleDeleteComp = async (film, compId) => {
    if (!window.confirm('Are you sure you want to delete this comp?')) {
      return;
    }

    try {
      const response = await apiService.delete(`/comps/${compId}`);
      
      if (response.success) {
        // Refresh comps for this film
        await fetchCompsForFilms([film]);
        // Refresh the films list to update counts
        await fetchFilms();
      } else {
        alert('Failed to delete comp');
      }
    } catch (err) {
      console.error('Error deleting comp:', err);
      alert('Failed to delete comp');
    }
  };

  const handleModalClose = () => {
    setShowCompModal(false);
    setSelectedFilm(null);
    setEditingComp(null);
    // Refresh data
    if (activeTab === 0) {
      fetchFilms();
    }
  };

  const handleAssignComp = async (assignmentData) => {
    try {
      const filmId = selectedFilm?.imdb_id || selectedFilm?.imdbId || selectedFilm?.tt_id;
      const response = await apiService.post('/comps/greenlight/assign', {
        primaryFilmId: filmId,
        compFilmId: assignmentData.imdbId,
        reason: assignmentData.reason,
        assignedBy: assignmentData.assignedBy
      });

      if (response.success) {
        handleModalClose();
      } else {
        throw new Error(response.error || 'Failed to assign comp');
      }
    } catch (err) {
      console.error('Error assigning comp:', err);
      throw err;
    }
  };

  const handleUpdateComp = async (updates) => {
    try {
      const response = await apiService.put(`/comps/greenlight/${editingComp.id}`, updates);

      if (response.success) {
        handleModalClose();
      } else {
        throw new Error(response.error || 'Failed to update comp');
      }
    } catch (err) {
      console.error('Error updating comp:', err);
      throw err;
    }
  };

  const handleFilmSelect = (film) => {
    const filmWithId = {
      ...film,
      imdb_id: film.imdbId || film.imdb_id,
      imdbId: film.imdbId || film.imdb_id,
      title_text: film.title || film.title_text,
      release_date: film.releaseDate || film.release_date
    };
    handleAddComp(filmWithId);
  };

  const getCompsCount = (film) => {
    const filmId = film.imdb_id || film.imdbId || film.tt_id;
    return compsMap[filmId]?.length || 0;
  };

  const formatReleaseDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Film Comps Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage film comps for upcoming and historical films
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Upcoming/Recent Films (Next 6 Weeks)" />
          <Tab label="Search Historical Films" />
        </Tabs>

        {/* Quick List Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Films (Next 6 Weeks + Previous 2 Weeks)</Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchFilms}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : films.length === 0 ? (
              <Alert severity="info">No films found in this date range.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Release Date</TableCell>
                      <TableCell>Comps</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {films.map((film) => {
                      const filmId = film.imdb_id || film.imdbId || film.tt_id;
                      const title = film.title_text || film.title || film.film_title || 'Unknown';
                      const compsCount = getCompsCount(film);
                      const comps = compsMap[filmId] || [];

                      return (
                        <TableRow key={filmId} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {title}
                            </Typography>
                            {filmId && (
                              <Typography variant="caption" color="text.secondary">
                                {filmId}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatReleaseDate(film.release_date || film.releaseDate)}</TableCell>
                          <TableCell>
                            <Chip
                              label={compsCount}
                              size="small"
                              color={compsCount > 0 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Add Comp">
                              <IconButton
                                size="small"
                                onClick={() => handleAddComp(film)}
                                color="primary"
                              >
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                            {comps.map((comp) => (
                              <React.Fragment key={comp.id}>
                                <Tooltip title="Edit Comp">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditComp(film, comp)}
                                    color="default"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Comp">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteComp(film, comp.id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </React.Fragment>
                            ))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Search Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Search for Historical Films
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Search for films to add comps. Click on a film to add a comp.
            </Typography>
            <FilmSearch onFilmSelect={handleFilmSelect} />
          </Box>
        )}
      </Paper>

      {/* Comp Assignment Modal */}
      {selectedFilm && (
        <CompAssignmentModal
          filmId={selectedFilm.imdb_id || selectedFilm.imdbId || selectedFilm.tt_id}
          filmTitle={selectedFilm.title_text || selectedFilm.title || selectedFilm.film_title || 'Unknown'}
          open={showCompModal}
          onClose={handleModalClose}
          onSubmit={editingComp ? handleUpdateComp : handleAssignComp}
          editingComp={editingComp}
        />
      )}
    </Box>
  );
};

export default FilmAdminComps;
